import { computed, makeObservable, observable, override } from 'mobx';
import moment from 'moment';
import { isAdmin } from '../../sdk/Utils';
import * as Api from '../../sdk/models';
import { FilteredPageCollectionController, PagedViewModel } from '../Collections';
import { UserSessionContext, ViewModel } from '../index';

export abstract class SocialMediaViewModel extends ViewModel {
	protected mConnections: Api.ISocialMediaConnection[];
	protected mPageCollection: Api.ISocialMediaPageCollection;
	protected mModel?: Api.ISocialMediaPost;

	protected abstract getBasePath(): string;
	protected abstract getUserAccountPath(): string;
	public abstract getConnectionType(): Api.SocialMediaType;

	constructor(userSession: UserSessionContext) {
		super(userSession);

		makeObservable<SocialMediaViewModel, 'mConnections' | 'mPageCollection' | 'mModel'>(this, {
			mConnections: observable.ref,
			mPageCollection: observable.ref,
			mModel: observable.ref,
			pages: computed,
			connection: computed,
			connections: computed,
			filterRequest: computed,
		});
		this.mConnections = userSession?.user?.socialMediaConnectedAccounts?.filter(
			x => x.type === this.getConnectionType()
		);
	}

	public get pages() {
		return this.mPageCollection?.pages;
	}

	public get connection() {
		return this.mConnections?.length > 0 ? this.mConnections[0] : null;
	}

	public get connections() {
		return this.mConnections;
	}

	public get filterRequest() {
		return this.mModel?.filterRequest;
	}

	public async getAuthUrl() {
		if (this.busy) {
			return;
		}

		this.busy = true;
		const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<string>(
			this.composeApiUrl({ urlPath: `${this.getBasePath()}/oauth/authorizeUrl` }),
			'POST'
		);

		this.busy = false;

		if (!opResult.success) {
			throw opResult;
		}

		return opResult;
	}

	public disconnect = async (connection: Api.ISocialMediaConnection) => {
		if (this.busy) {
			return;
		}

		this.busy = true;
		const opResult = await this.userSession.webServiceHelper.callWebServiceAsync<Api.IUser>(
			this.composeApiUrl({ urlPath: `${this.getUserAccountPath()}` }),
			'DELETE',
			connection
		);
		this.busy = false;

		if (!opResult.success) {
			throw opResult;
		}

		if (this.userSession.user.id === opResult.value.id) {
			this.userSession.updateUser(opResult.value);
		}

		this.mConnections = opResult.value.socialMediaConnectedAccounts.filter(x => x.type === this.getConnectionType());

		return opResult;
	};

	public loadPages = async (connection?: Api.ISocialMediaConnection) => {
		if (this.busy) {
			return;
		}

		this.busy = true;
		const opResult = await this.userSession.webServiceHelper.callWebServiceAsync<Api.ISocialMediaPageCollection>(
			this.composeApiUrl({ urlPath: `${this.getBasePath()}/account` }),
			'POST',
			connection
		);
		this.busy = false;

		if (!opResult.success) {
			throw opResult;
		}

		this.mPageCollection = opResult.value;

		return opResult;
	};

	public setPostTargetPage = async (page: Api.ISocialMediaPage, connection: Api.ISocialMediaConnection) => {
		if (this.busy) {
			return;
		}

		const pageSelection: Api.ISocialMediaPageSelection = {
			pageId: page.id,
			pageName: page.name,
			userId: this.mPageCollection.accountId,
		};

		const request: Api.ISetPostTargetRequest = {
			connection,
			pageSelection,
		};

		this.busy = true;
		const opResult = await this.userSession.webServiceHelper.callWebServiceAsync<Api.IUser>(
			this.composeApiUrl({ urlPath: `${this.getUserAccountPath()}/postTargetPage` }),
			'PUT',
			request
		);
		this.busy = false;

		if (!opResult.success) {
			throw opResult;
		}

		if (this.userSession.user.id === opResult.value.id) {
			this.userSession.updateUser(opResult.value);
		}

		this.mConnections = opResult.value.socialMediaConnectedAccounts.filter(x => x.type === this.getConnectionType());

		return opResult;
	};

	public resetConnectionsToUsers(userSession: UserSessionContext, connection: Api.ISocialMediaConnection) {
		this.mConnections = userSession?.user?.socialMediaConnectedAccounts?.filter(
			x => x.type === this.getConnectionType() && x.postTargetId !== connection.postTargetId
		);
	}

	/**
	 * Refresh connections from the user session.
	 * This is useful when the user session has been updated by another part of the app.
	 */
	public refreshConnectionsFromUserSession() {
		this.mConnections = this.userSession?.user?.socialMediaConnectedAccounts?.filter(
			x => x.type === this.getConnectionType()
		);
	}
}

export class FacebookViewModel extends SocialMediaViewModel {
	public getConnectionType() {
		return Api.SocialMediaType.Facebook;
	}

	protected getUserAccountPath(): string {
		return 'user/socialMedia/facebook';
	}

	protected getBasePath(): string {
		return 'social/facebook';
	}
}

export class InstagramViewModel extends SocialMediaViewModel {
	public getConnectionType() {
		return Api.SocialMediaType.Instagram;
	}

	protected getBasePath(): string {
		return 'social/instagram';
	}

	protected getUserAccountPath(): string {
		return 'user/socialMedia/instagram';
	}
}

export class LinkedInViewModel extends SocialMediaViewModel {
	public getConnectionType() {
		return Api.SocialMediaType.LinkedIn;
	}

	protected getBasePath(): string {
		return 'social/linkedin';
	}

	protected getUserAccountPath(): string {
		return 'user/socialMedia/linkedin';
	}
}

export class SocialMediaPostViewModel extends ViewModel {
	protected mSocialPost: Partial<Api.ISocialMediaPost>;

	public static schedulePost = async (
		userSession: UserSessionContext,
		postSocialMediaRequest: Api.ICreateSocialMediaPostRequest,
		suggestionId?: string,
		impersonationContext?: Api.IImpersonationContext
	) => {
		const opResult = await userSession.webServiceHelper.callWebServiceAsync<Api.ISocialMediaPost>(
			this.composeApiUrl({
				impersonationContext,
				queryParams: {
					contentCalendarSuggestionId: suggestionId,
					forUserId: postSocialMediaRequest?.forUserId,
				},
				urlPath: 'social/post',
			}),
			'POST',
			postSocialMediaRequest
		);

		if (!opResult.success) {
			throw opResult;
		}

		return new SocialMediaPostViewModel(userSession, opResult.value);
	};

	constructor(userSession: UserSessionContext, post: Partial<Api.ISocialMediaPost>) {
		super(userSession);

		makeObservable<SocialMediaPostViewModel, 'mSocialPost'>(this, {
			mSocialPost: observable.ref,
			id: computed,
			citation: computed,
			anonymousAccessLink: computed,
			approvalRequests: computed,
			actor: computed,
			targets: computed,
			designatedTargets: computed,
			status: computed,
			cancelledMoment: computed,
			completedMoment: computed,
			creator: computed,
			dueDate: computed,
			lastModifiedDate: computed,
			attachments: computed,
			content: computed,
			dueMoment: computed,
			hasEditableStatus: computed,
			canEditAsAdmin: computed,
			canEdit: computed,
			name: computed,
			sendWithCompliance: computed,
			templateReference: computed,
			overlayLogo: computed,
		});

		this.mSocialPost = post;
	}

	public get id() {
		return this.mSocialPost?.id;
	}

	public get citation() {
		return this.mSocialPost?.citation;
	}

	public get anonymousAccessLink() {
		return this.mSocialPost?.anonymousAccessLink;
	}

	get approvalRequests() {
		return this.mSocialPost?.approvalRequests;
	}

	get actor() {
		return this.mSocialPost?.actor;
	}

	public get targets() {
		return this.mSocialPost?.targets;
	}

	public get designatedTargets() {
		return this.mSocialPost?.designatedTargets;
	}

	public get status() {
		return this.mSocialPost?.status;
	}

	public get cancelledMoment() {
		return this.mSocialPost?.cancelledDate ? moment(this.mSocialPost.cancelledDate) : null;
	}

	public get completedMoment() {
		return this.mSocialPost?.completedDate ? moment(this.mSocialPost.cancelledDate) : null;
	}

	get creator() {
		return this.mSocialPost?.creator;
	}

	public get dueDate() {
		return this.mSocialPost?.dueDate;
	}

	public get lastModifiedDate() {
		return this.mSocialPost?.lastModifiedDate;
	}

	public get attachments() {
		return this.mSocialPost?.attachments;
	}

	public get content() {
		return this.mSocialPost?.content;
	}

	public get dueMoment() {
		return this.mSocialPost?.dueDate ? moment(this.mSocialPost.dueDate) : null;
	}

	public get hasEditableStatus() {
		return (
			this.mSocialPost?.status === Api.PostStatus.Scheduled ||
			this.mSocialPost.status === Api.PostStatus.Draft ||
			this.mSocialPost.status === Api.PostStatus.Pending
		);
	}

	public get canEditAsAdmin() {
		return this.mSocialPost.status === Api.PostStatus.Pending && this.isAdmin;
	}

	/** Like this.hasEditableStatus but factors in creator */
	public get canEdit() {
		const userIsAdmin = isAdmin(this.mUserSession.user);
		return (
			this.hasEditableStatus &&
			!this.mImpersonationContext &&
			(userIsAdmin || this.mUserSession?.user?.id === this.mSocialPost?.creator?.id)
		);
	}

	public get name() {
		return this.mSocialPost?.name;
	}

	public get sendWithCompliance() {
		return this.mSocialPost?.sendWithCompliance;
	}

	public get templateReference() {
		return this.mSocialPost?.templateReference;
	}

	public get overlayLogo() {
		return this.mSocialPost?.overlayLogo;
	}

	public toJs = () => {
		return this.mSocialPost as Api.ISocialMediaPost;
	};

	public load = async () => {
		if (this.busy) {
			return;
		}

		this.loading = true;
		const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.ISocialMediaPost>(
			this.composeApiUrl({ urlPath: `social/post/${this.id}` }),
			'GET'
		);
		this.loading = false;

		if (!opResult.success) {
			throw opResult;
		}

		this.mSocialPost = opResult.value;
		return opResult.value;
	};

	public update = async (postSocialMedia: Api.ISocialMediaPost) => {
		const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.ISocialMediaPost>(
			this.composeApiUrl({
				queryParams: {
					forUserId: postSocialMedia?.creator?.id,
				},
				urlPath: `social/post/${this.mSocialPost.id}`,
			}),
			'PUT',
			postSocialMedia
		);

		if (!opResult.success) {
			throw opResult;
		}

		this.mSocialPost = opResult.value;
		return opResult;
	};

	public cancel = async () => {
		const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.ISocialMediaPost>(
			this.composeApiUrl({ urlPath: `social/post/${this.id}` }),
			'DELETE'
		);
		if (!opResult.success) {
			throw opResult;
		}
		return opResult;
	};

	public approve = async () => {
		if (this.isBusy || this.mSocialPost.approvalRequests[0]?.response) {
			return;
		}

		this.busy = true;
		const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.ISocialMediaPost>(
			this.composeApiUrl({ urlPath: `social/post/${this.id}/approve` }),
			'POST'
		);
		this.busy = false;

		if (!opResult.success) {
			throw opResult;
		}
		return opResult;
	};

	public reject = async () => {
		if (this.isBusy || this.mSocialPost.approvalRequests[0]?.response) {
			return;
		}

		this.busy = true;
		const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.ISocialMediaPost>(
			this.composeApiUrl({ urlPath: `social/post/${this.id}/reject` }),
			'POST'
		);
		this.busy = false;

		if (!opResult.success) {
			throw opResult;
		}
		return opResult;
	};
}

export class SocialMediaPostReportViewModel extends ViewModel {
	protected mSocialMediaPostReport: Partial<Api.ISocialMediaPostReport>;

	constructor(userSession: UserSessionContext, postReport: Partial<Api.ISocialMediaPostReport>) {
		super(userSession);

		makeObservable<SocialMediaPostReportViewModel, 'mSocialMediaPostReport'>(this, {
			mSocialMediaPostReport: observable.ref,
			id: computed,
			anonymousAccessLink: computed,
			approvalRequests: computed,
			name: computed,
			actor: computed,
			targets: computed,
			designatedTargets: computed,
			status: computed,
			dueDate: computed,
			creator: computed,
			cancelledDate: computed,
			completedDate: computed,
			lastModifiedDate: computed,
			templateReference: computed,
			hasEditableStatus: computed,
			adminEditStatus: computed,
			canEdit: computed,
		});

		this.mSocialMediaPostReport = postReport;
	}

	public get id() {
		return this.mSocialMediaPostReport?.id;
	}

	public get anonymousAccessLink() {
		return this.mSocialMediaPostReport?.anonymousAccessLink;
	}

	public get approvalRequests() {
		return this.mSocialMediaPostReport?.approvalRequests;
	}

	public get name() {
		return this.mSocialMediaPostReport?.name;
	}

	public get actor() {
		return this.mSocialMediaPostReport?.actor;
	}

	public get targets() {
		return this.mSocialMediaPostReport?.targets;
	}

	public get designatedTargets() {
		return this.mSocialMediaPostReport?.designatedTargets;
	}

	public get status() {
		return this.mSocialMediaPostReport?.status;
	}

	public get dueDate() {
		return this.mSocialMediaPostReport?.dueDate;
	}

	public get creator() {
		return this.mSocialMediaPostReport?.creator;
	}

	public get cancelledDate() {
		return this.mSocialMediaPostReport?.cancelledDate;
	}

	public get completedDate() {
		return this.mSocialMediaPostReport?.completedDate;
	}

	public get lastModifiedDate() {
		return this.mSocialMediaPostReport?.lastModifiedDate;
	}

	public get templateReference() {
		return this.mSocialMediaPostReport?.templateReference;
	}

	public get hasEditableStatus() {
		return (
			this.mSocialMediaPostReport.status === Api.PostStatus.Scheduled ||
			this.mSocialMediaPostReport.status === Api.PostStatus.Draft ||
			this.mSocialMediaPostReport.status === Api.PostStatus.Pending
		);
	}

	public get adminEditStatus() {
		return (
			this.mSocialMediaPostReport.status === Api.PostStatus.Pending &&
			(this.mUserSession?.user?.role === 'admin' || this.mUserSession?.user?.role === 'superAdmin')
		);
	}

	/** Like this.hasEditableStatus but factors in creator when not impersonating */
	public get canEdit() {
		const userIsAdmin = isAdmin(this.mUserSession.user);
		return (
			this.hasEditableStatus &&
			!this.mImpersonationContext &&
			(userIsAdmin || this.mUserSession?.user?.id === this.mSocialMediaPostReport?.creator?.id)
		);
	}

	public toJs = () => {
		return this.mSocialMediaPostReport;
	};

	public setPostReport = (report: Api.ISocialMediaPostReport) => {
		this.mSocialMediaPostReport = report;
	};

	public loadPost = async () => {
		if (this.busy) {
			return;
		}

		this.busy = true;
		const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.ISocialMediaPost>(
			this.composeApiUrl({ urlPath: `social/post/${this.id}` }),
			'GET'
		);
		this.busy = false;

		if (!opResult.success) {
			throw opResult;
		}

		return opResult.value;
	};

	public load = async () => {
		if (this.busy) {
			return;
		}
		this.busy = true;

		const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<Api.ISocialMediaPostReport>(
			this.composeApiUrl({ urlPath: `reports/post/${this.id}` }),
			'GET'
		);
		this.busy = false;

		if (!opResult.success) {
			throw opResult;
		}

		this.mSocialMediaPostReport = opResult.value;
		return opResult.value;
	};
}

export class SocialMediaPostsReportingViewModel extends PagedViewModel<
	Api.ISocialMediaPostReport,
	SocialMediaPostReportViewModel,
	Api.ISocialMediaReportRequest
> {
	protected mSelectedUserId: string = undefined;
	protected mSelectedPostStatus: Api.PostStatus[];
	public pageSize: number;
	public dateRange: { startDate?: Date; endDate?: Date };
	public resourceSelectorId: string;
	public declare mPagedCollectionController: FilteredPageCollectionController<
		Api.ISocialMediaPostReport,
		SocialMediaPostReportViewModel,
		Api.ISocialMediaReportRequest
	>;

	constructor(userSession: UserSessionContext, user?: Partial<Api.IUser>) {
		super(userSession);

		makeObservable<SocialMediaPostsReportingViewModel, 'mSelectedUserId' | 'mSelectedPostStatus'>(this, {
			mSelectedUserId: observable,
			mSelectedPostStatus: observable,
			pageSize: observable,
			dateRange: observable.ref,
			resourceSelectorId: observable.ref,
			mPagedCollectionController: observable,
			isBusy: override,
			request: computed,
		});

		this.reset = this.reset.bind(this);
		this.mSelectedUserId = user?.id || (this.isAdmin ? undefined : userSession.user.id);
		this.mSelectedPostStatus = null;
		this.mSocialMediaPostTransformer = this.mSocialMediaPostTransformer.bind(this);
		this.pageSize = 25;

		this.mPagedCollectionController = new FilteredPageCollectionController<
			Api.ISocialMediaPostReport,
			SocialMediaPostReportViewModel,
			Api.ISocialMediaReportRequest
		>({
			apiPath: this.apiPathBase,
			client: this.mUserSession.webServiceHelper,
			itemUniqueIdentifierPropertyPath: 'id',
			transformer: this.mSocialMediaPostTransformer,
		});

		this.dateRange = {
			endDate: moment().add(180, 'days').endOf('day').toDate(),
			startDate: moment().subtract(180, 'days').startOf('day').toDate(),
		};
	}

	public get isBusy() {
		return this.mPagedCollectionController.isFetching;
	}

	public get request(): Api.ISocialMediaReportRequest {
		const request = {
			endDate: this.dateRange?.endDate,
			startDate: this.dateRange?.startDate,
			status: this.mSelectedPostStatus,
			userId: this.mSelectedUserId,
		};

		return request;
	}

	protected mSocialMediaPostTransformer(post: Api.ISocialMediaPostReport) {
		return new SocialMediaPostReportViewModel(this.userSession, post).impersonate(this.mImpersonationContext);
	}

	public readonly apiPathBase = 'reports/post';

	public setSelectedUserId = (userId: string) => {
		this.mSelectedUserId = userId;
		return this;
	};

	public setDateRange = (range: { startDate?: Date; endDate?: Date }) => {
		this.dateRange = range;
		return this;
	};

	public setSelectedPostStatus = (postStatus: Api.PostStatus[]) => {
		this.mSelectedPostStatus = postStatus?.length > 0 ? postStatus : null;
		return this;
	};

	public setPageSize = (size: number) => {
		this.pageSize = size;
		return this;
	};

	public impersonate(impersonationContext?: Api.IImpersonationContext) {
		super.impersonate(impersonationContext);
		this.mPagedCollectionController.impersonate(impersonationContext);
		return this;
	}

	public reset(): void {
		super.reset();

		this.mSelectedUserId = null;
		this.mSelectedPostStatus = [];
	}
}
